import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification, setConnectionStatus } from '../store/slices/notificationSlice';
import { lakehouseApiSlice } from '../api/lakehouseApiSlice';
import { buildVaultiscanHeaders, getApiBaseUrl } from '../api/requestUtils';
import { RootState } from '../store';
import { ExtendedAccountResponse } from '../store/types';

export const useNotificationStream = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  // Get customerId from account.user.customer_id or account.customer_id, consistent with apiSlice

  const account = auth.account as ExtendedAccountResponse;
  const customerId = account?.customer_id || account?.user?.customer_id;

  // const customerId = auth.account?.user?.customer_id || auth.account?.customer_id;

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const baseUrl = getApiBaseUrl();
    const streamUrl = `${baseUrl}/api/notifications/stream`;

    const connect = async () => {
      // Abort previous connection if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      dispatch(setConnectionStatus('connecting'));

      try {
        const headers: HeadersInit = buildVaultiscanHeaders({
          customerId: customerId ? String(customerId) : undefined,
          accept: 'text/event-stream',
        });

        const response = await fetch(streamUrl, {
          method: 'GET',
          headers,
          credentials: 'include',
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        dispatch(setConnectionStatus('connected'));

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader available');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          const lines = buffer.split('\n');
          // Keep the last part in buffer if it doesn't end with a newline
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.trim().startsWith('data: ')) {
              try {
                const jsonStr = line.trim().slice(6);
                const data = JSON.parse(jsonStr);

                // Handle 'ping' or other control messages
                if (data.type === 'ping') continue;

                // Dispatch new notification
                if (data.type == 'notification' && data.payload?.id && data.payload?.message) {
                  dispatch(
                    addNotification({
                      notification_id: data.payload.id,
                      message: data.payload.message,
                      notification_type: data.payload.type,
                      delivery_status: 'sent', // Initially sent/pending
                      priority: data.payload.priority || 'medium',
                      created_at: data.payload.created_at || new Date().toISOString(),
                      entity_type: data.payload.entity_type,
                      action_type: data.payload.action_type,
                      metadata: data.payload.metadata,
                    }),
                  );

                  // Invalidate cache if applicable
                  const { entity_type, action_type, metadata } = data.payload;
                  if (
                    entity_type === 'data_processing_job' &&
                    (action_type === 'completed' || action_type === 'failed') &&
                    metadata?.target_lakehouse_layer_id
                  ) {
                    dispatch(
                      lakehouseApiSlice.util.invalidateTags([
                        { type: 'DataTable', id: metadata.target_lakehouse_layer_id },
                      ]),
                    );
                  }
                }
              } catch (error) {
                console.error('Error parsing SSE message:', error);
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError') return;
        console.error('SSE Error:', error);
        dispatch(setConnectionStatus('disconnected'));

        // Optional: Implement retry logic here
      }
    };

    connect();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      dispatch(setConnectionStatus('disconnected'));
    };
  }, [dispatch, customerId]);
};
